import {Auditing} from './auditing.model';
import {Attachment} from './attachment.model';
import {BaseTask} from './base-task.model';
import {DocumentGenerationEntry} from './document-generation-entry.model';
import {CustomField, CustomFieldOption, isSmartFieldType} from './custom-field.model';
import {FormLayout, SizeHint} from './intake-layout.model';
import {CaseStatus} from '@ee/common/enums';
import {ChoiceLayout, CustomFieldType, RenderSeparator, TextFormat} from '@ee/common/enums';
import {SubjectType} from '@ee/common/enums';
import {PublicationStatus} from '@ee/common/enums';

/**
 * A single entry in a {@link CaseTemplate} field registry — the source of truth
 * for a case type. Projections (intake, sidebar, documents, import) reference
 * fields by their immutable {@link key}. Mirrors the backend `FieldDefinition`.
 */
export interface FieldDefinition {
  key: string;
  label?: string;
  type?: CustomFieldType;
  options?: string[];
  /**
   * MULTI_SELECT only — the {value, label} pairs behind `options`. Optional and additive: absent
   * means each `options` entry is an option whose label equals its value (Req 2.2). `options` stays
   * equal to the ordered list of these values, so every existing reader of it is unaffected.
   */
  option_definitions?: CustomFieldOption[];
  /** MULTI_SELECT only — how selections join into one string in documents/reports. Null ⇒ COMMA. */
  render_separator?: RenderSeparator | null;
  /** RADIO and MULTI_SELECT only — choices stacked or lined up and wrapped. Absent ⇒ VERTICAL. */
  choice_layout?: ChoiceLayout | null;
  /**
   * The value a control is pre-filled with on a form for a **new** case. Absent ⇒ no default. For a
   * DATE field it is the `TODAY` or `BLANK` sentinel or an ISO date; validated server-side against the
   * field's type and options by `FieldRegistryValidator`.
   */
  default_value?: any;
  required?: boolean;
  built_in?: boolean;
  removable?: boolean;
  hidden?: boolean;
  calculated?: boolean;
  calculated_expression?: string;
  /** True for currency fields — the UI renders them as `$X,XXX.XX`. */
  monetary?: boolean;
  /** When true, this field's amount(s) sum into the case claim total (DECIMAL scalar or RENT_HISTORY months). */
  include_in_total?: boolean;
  /** Subtracted from, rather than added to, a section total (partial payments). */
  subtract_from_total?: boolean;
  /** TEXT only — email or web-link validation. */
  format?: TextFormat | null;
  group?: string;
  help_text?: string;
  /** Expanded hint shown in a tooltip when the hint is long (modal "Expanded Hint"). */
  help_more_info?: string;
  /** What the field contains; shown in document-template field lists. */
  description?: string;
  /** Example populated value, shown in document-template field lists. */
  example?: string;
  validation?: Record<string, any>;
  /**
   * How wide this field usually wants to be — a hint, not a width. It seeds a new placement's span and
   * then stops applying, so changing it moves every placement that still inherits and leaves overridden
   * ones alone. That is the whole reason span belongs to the placement rather than to the definition.
   */
  size_hint?: SizeHint;
  subject_collection?: boolean;
  /** Party kind for a subject collection (absent ⇒ INDIVIDUAL). */
  subject_type?: SubjectType;
  /** Singular label for one subject (e.g. "Tenant", "Defendant"). */
  subject_label_singular?: string;
  /** Nested sub-field schema for each subject (one level deep). */
  subject_fields?: FieldDefinition[];
  /** Provenance: the org PartySchema this collection's sub-fields were copied from (enables refresh). */
  subject_schema_id?: string;
}

/**
 * Built-in case fields that hold money and should render as currency (`$X,XXX.XX`). Built-in fields are
 * form-derived in the case views (not from the registry), so they don't carry the backend `monetary` flag —
 * this key set is the source of truth for the UI. `interest_rate` is intentionally excluded (it's a %).
 */
export const MONETARY_CASE_FIELD_KEYS = new Set<string>([
  'past_due_rent', 'late_fees', 'damages', 'costs', 'court_costs', 'civil_recovery_fees',
  'attorney_fee', 'judgement_amount', 'lease_rent_amount', 'security_deposit', 'partial_payments'
]);

/**
 * Maps a registry {@link FieldDefinition} into the `CustomField` shape the shared
 * field-editor modal consumes (for editing an existing field).
 */
export function fieldDefinitionToCustomField(fd: FieldDefinition): CustomField {
  return {
    label: fd.label || fd.key,
    field: fd.key,
    type: fd.type || CustomFieldType.TEXT,
    options: fd.options,
    // Only for a type that has them, so no other field gains the two new properties (Req 1.4).
    ...(fd.option_definitions?.length ? {option_definitions: fd.option_definitions} : {}),
    ...(fd.render_separator ? {render_separator: fd.render_separator} : {}),
    ...(fd.choice_layout ? {choice_layout: fd.choice_layout} : {}),
    // A registry field has no `mask`: the backend `FieldDefinition` carries none, and the PM case form
    // is SurveyJS-rendered. Masks are a `CustomField` (org custom field / attorney intake) concept.
    ...(fd.default_value !== null && fd.default_value !== undefined ? {default_value: fd.default_value} : {}),
    required: fd.required,
    value: fd.calculated_expression,
    hint: fd.help_text,
    hint_more_info: fd.help_more_info,
    description: fd.description,
    example: fd.example,
    hide: fd.hidden,
    include_in_total: fd.include_in_total,
    subtract_from_total: fd.subtract_from_total,
    monetary: fd.monetary,
    ...(fd.format ? {format: fd.format} : {}),
    size_hint: fd.size_hint,
    hide_from_intake: false,
    prevent_removal: fd.removable === false,
    built_in_field: fd.built_in
  };
}

/**
 * Maps the shared modal's `CustomField` result back into a registry
 * {@link FieldDefinition}, preserving immutable attributes (key/built_in/removable)
 * of the [existing] definition when editing.
 */
export function customFieldToFieldDefinition(cf: CustomField, existing?: FieldDefinition): FieldDefinition {
  const calculated = isSmartFieldType(cf.type);
  const definition: FieldDefinition = {
    ...(existing || {}),
    key: existing?.key ?? cf.field ?? '',
    label: cf.label,
    type: cf.type,
    options: cf.options,
    required: cf.required,
    hidden: cf.hide,
    removable: cf.prevent_removal ? false : (existing?.removable ?? true),
    built_in: existing?.built_in ?? cf.built_in_field ?? false,
    calculated,
    calculated_expression: calculated ? cf.value : existing?.calculated_expression,
    include_in_total: cf.include_in_total,
    subtract_from_total: cf.subtract_from_total,
    monetary: cf.monetary ?? existing?.monetary,
    format: cf.type === CustomFieldType.TEXT ? (cf.format ?? null) : null,
    help_text: cf.hint,
    help_more_info: cf.hint_more_info,
    description: cf.description,
    example: cf.example,
    size_hint: cf.size_hint ?? existing?.size_hint
  };
  // Set only for a type that has them, and cleared when a field is retyped away from MULTI_SELECT —
  // otherwise the spread of `existing` above would keep stale definitions on a plain text field, and
  // every other field would gain the two new properties on its next save (Req 1.4).
  if (cf.option_definitions?.length) {
    definition.option_definitions = cf.option_definitions;
  } else {
    delete definition.option_definitions;
  }
  if (cf.render_separator) {
    definition.render_separator = cf.render_separator;
  } else {
    delete definition.render_separator;
  }
  // Stacked is the default, so only a lined-up layout is stored; retyping away from a choice field or
  // switching back to stacked clears it rather than letting the `existing` spread keep it.
  if (cf.choice_layout === ChoiceLayout.HORIZONTAL) {
    definition.choice_layout = cf.choice_layout;
  } else {
    delete definition.choice_layout;
  }
  // Same treatment, and for the same reason: without the delete, the `existing` spread would keep a
  // default the user has just cleared, and one retyped into a field type the server rejects would fail
  // the whole template save with `default.invalid`.
  if (cf.default_value !== null && cf.default_value !== undefined && cf.default_value !== '') {
    definition.default_value = cf.default_value;
  } else {
    delete definition.default_value;
  }
  return definition;
}

/**
 * Full month names for a RENT_HISTORY row's month dropdown. The choice **value** is the 0-indexed
 * month (0 = January), matching the backend `PastDueRentHistory.month` and the pdrh_* document tokens.
 */
export const RENT_HISTORY_MONTH_CHOICES: { value: number; text: string }[] = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
].map((text, value) => ({value, text}));

/**
 * Default nested sub-field schema for a subject collection, by {@link SubjectType}.
 * Mirrors the backend `SubjectSchemas`. Seeds a new subject collection's
 * `subject_fields` (and its SurveyJS paneldynamic template).
 */
const INDIVIDUAL_SUBJECT_FIELDS: FieldDefinition[] = [
  {key: 'first_name', label: 'First Name', type: CustomFieldType.TEXT, built_in: true, required: true},
  {key: 'middle_name', label: 'Middle Name', type: CustomFieldType.TEXT, built_in: true},
  {key: 'last_name', label: 'Last Name', type: CustomFieldType.TEXT, built_in: true, required: true},
  {key: 'phone', label: 'Phone Number', type: CustomFieldType.TEXT, built_in: true},
  {key: 'email', label: 'Email', type: CustomFieldType.TEXT, built_in: true},
  {key: 'dob', label: 'Date of Birth', type: CustomFieldType.DATE, built_in: true},
  {key: 'ssn', label: 'SSN', type: CustomFieldType.TEXT, built_in: true}
];

const ORGANIZATION_SUBJECT_FIELDS: FieldDefinition[] = [
  {key: 'legal_name', label: 'Legal Name', type: CustomFieldType.TEXT, built_in: true, required: true},
  {key: 'dba', label: 'DBA / Trade Name', type: CustomFieldType.TEXT, built_in: true},
  {key: 'ein', label: 'EIN', type: CustomFieldType.TEXT, built_in: true},
  {key: 'registered_agent', label: 'Registered Agent', type: CustomFieldType.TEXT, built_in: true},
  {key: 'phone', label: 'Phone Number', type: CustomFieldType.TEXT, built_in: true},
  {key: 'email', label: 'Email', type: CustomFieldType.TEXT, built_in: true}
];

export function defaultSubjectFields(type: SubjectType): FieldDefinition[] {
  const src = type === SubjectType.ORGANIZATION ? ORGANIZATION_SUBJECT_FIELDS : INDIVIDUAL_SUBJECT_FIELDS;
  return src.map(f => ({...f}));
}

/**
 * Reconcile an existing sidebar layout against the current registry: drop field keys
 * that no longer exist and de-dupe. Fields not in any section are left **unclaimed**
 * (see {@link unplacedKeys}); they are never auto-shoved into a section.
 */
export function reconcileSidebar(projection: SidebarProjection | undefined, registryKeys: string[]): SidebarProjection {
  const keySet = new Set(registryKeys);
  const placed = new Set<string>();
  const sections: SidebarSection[] = (projection?.sections || []).map(s => ({
    ...s,
    field_keys: (s.field_keys || []).filter(k => {
      if (keySet.has(k) && !placed.has(k)) {
        placed.add(k);
        return true;
      }
      return false;
    })
  }));
  return {sections};
}

/** Registry field keys not placed in any sidebar section (the "unclaimed" palette). */
export function unplacedKeys(projection: SidebarProjection | undefined, registryKeys: string[]): string[] {
  const placed = new Set<string>();
  for (const s of projection?.sections || []) {
    for (const k of s.field_keys || []) {
      placed.add(k);
    }
  }
  return registryKeys.filter(k => !placed.has(k));
}

/**
 * The default sidebar for a template with no saved one: one section per layout section, in page order,
 * holding the fields placed there that the registry knows. A section with none is skipped; a key placed
 * twice is claimed by its first section. What is left over stays unclaimed (see {@link unplacedKeys}).
 */
export function deriveSidebarFromLayout(layout: FormLayout | null | undefined, registryKeys: string[]): SidebarProjection {
  const keySet = new Set(registryKeys);
  const placed = new Set<string>();
  const byKey = new Map((layout?.sections ?? []).map(section => [section.key, section]));
  const listed = (layout?.pages ?? []).flatMap(page => page.section_keys);
  const ordered = [
    ...listed.map(key => byKey.get(key)).filter(section => !!section),
    ...(layout?.sections ?? []).filter(section => !listed.includes(section.key))
  ];
  const sections: SidebarSection[] = [];
  for (const section of ordered) {
    const keys = section.rows
      .flatMap(row => row.items)
      .filter(item => item.kind === 'FIELD' && !!item.field_key)
      .map(item => item.field_key)
      .filter(key => keySet.has(key) && !placed.has(key));
    keys.forEach(key => placed.add(key));
    if (keys.length) {
      sections.push({key: section.key, title: section.title || 'Section', collapsed: false, field_keys: keys});
    }
  }
  return {sections};
}

export interface SidebarSection {
  /**
   * A stable identifier a rule can target.
   *
   * Optional because no panel section has ever had one: sections are identified by position today, and
   * their titles are author-editable and can collide. Rules therefore match a *section* only where a key
   * exists, and match by field membership otherwise — an undercount, but never the wrong section. A
   * title-derived key would reintroduce exactly the cross-case-type misfire the rule model exists to
   * prevent.
   */
  key?: string;
  title?: string;
  collapsed?: boolean;
  /** Ordered registry field keys placed in this section. */
  field_keys: string[];
  /**
   * Section kind. Absent/`'fields'` (default) = a normal field-layout section. `'documents'`
   * = a pinned system section that renders the case's aggregated document list (host-provided
   * template) instead of fields; it can be reordered but not removed, and holds no field keys.
   */
  kind?: 'fields' | 'documents';
  /**
   * Firm-only sections are for staff. The case panel states which sections the client can see, because
   * that is not inferable from the panel itself and it is the fact people most need before typing into
   * one. Absent means the client can see it, which matches how the panel has always behaved.
   */
  firm_only?: boolean;
}

/** Sidebar layout: sections of ordered registry field keys. */
export interface SidebarProjection {
  sections: SidebarSection[];
}

export interface CaseStepTask extends BaseTask {
  required?: boolean;
  remove_on_step_completion?: boolean;
}

/** Mirrors backend `StepProgressStatus` (serialized uppercase). */
export type CaseStepStatus = 'STARTED' | 'COMPLETED' | 'SKIPPED' | 'UNDONE';

/**
 * Per-step progress embedded on a {@link Case}, keyed by the immutable
 * `step_key`. Mirrors backend `CaseStepProgress`. Tier B (requires P1).
 */
export interface CaseStepProgress {
  step_key: string;
  status: CaseStepStatus;
  started_at?: string;
  completed_at?: string;
  actor?: string;
  step_data?: Record<string, any>;
  /** User-defined or auto-computed due date for this step (ISO-8601). */
  deadline?: string;
  /** Ids of the users this step is assigned to (explicit per-step assignees; rolled up to the case). */
  assigned_user_ids?: string[];
  /** True when written by a workflow migration (auto-skipped steps before the chosen start step). */
  migration?: boolean;
}

/**
 * A backend case-activity audit entry. Mirrors the `CaseActivity` domain
 * (requires P3 — `GET /api/v1/cases/{id}/activity`).
 */
export interface CaseActivity {
  id: string;
  case_id: string;
  activity_type: string;
  summary?: string;
  actor?: string;
  step_key?: string;
  subject_key?: string;
  workflow_id?: string;
  workflow_version?: number;
  detail?: Record<string, any>;
  occurred_at: string;
}

/**
 * The recognized case-activity item families rendered by the case Activity Log.
 * Mirrors the backend `CaseActivityType` values the requirements name. Entries
 * whose `activity_type` is not one of these are treated as unrecognized and are
 * dropped upstream rather than rendered. The family → icon map (single source of
 * truth for the per-family icon) lives in the case-management module.
 */
export type CaseActivityFamily =
  | 'MESSAGE'
  | 'NOTE_ADDED'
  | 'ATTACHMENT_ADDED'
  | 'STEP_STARTED'
  | 'STEP_COMPLETED'
  | 'STEP_SKIPPED'
  | 'TASK_ACTIVITY'
  | 'COURT_DATE'
  | 'CASE_OPENED'
  | 'CASE_CLOSED'
  | 'CASE_REOPENED'
  | 'DOCUMENT_GENERATED'
  | 'FILING_REVIEWED';

/**
 * A SCRA/PACER check result for a tenant subject on a case. Mirrors the
 * `CaseCheck` domain (requires P4 — `GET /api/v1/cases/{id}/checks`).
 */
export interface CaseCheck {
  id: string;
  case_id: string;
  subject_key: string;
  check_type: string;
  status: string;
  latest?: boolean;
  result?: Record<string, any>;
  requested_by?: string;
  run_at: string;
}

export interface CaseWorkflowStep {
  id: string;
  step: number;
  name: string;
  description?: string;
  icon?: string;
  optional?: boolean;
  /** Go/No-Go decision step — while active, the case detail surfaces the decision actions. */
  decision?: boolean;
  disabled?: boolean;
  // Tier B (P1): immutable step key used to correlate with step_progress and to
  // drive key-based step actions. Optional → legacy cases fall back to `step`.
  step_key?: string;
  // Tier B (P5): per-step assignee role surfaced from the workflow step.
  assignee_role?: string;
  survey_id?: string;
  survey_definition?: string;
  step_data: Record<string, any>;
  document_templates: string[];
  /**
   * The unified Document_Generation_List for this step, resolved by the backend's
   * `resolveStepsForDisplay` (late-bound via `liveStepConfig`, fallback to the version
   * snapshot). Lets the interactive Case step-completion control pre-populate the
   * override selector. Optional/additive — absent on legacy/non-document steps.
   */
  document_generation?: DocumentGenerationEntry[];
  assigned_users: string[];
  tasks: CaseStepTask[];
  date_started?: string;
  date_completed?: string;
  skipped?: boolean;
  expected_days?: number;
  email_on_completion?: boolean;
  comment?: string;
}

export interface CaseNote {
  id: string;
  text: string;
  created_by: string;
  created_by_name: string;
  created_date: string;
  /** EE-531: "EasyEviction support (on behalf of X)" for a note added in a support session, else the author's name. */
  actor_display?: string | null;
  /** EE-531: true when the note was added in a support session. The admin's email is never sent. */
  impersonated?: boolean;
}

export enum CaseLinkType {
  RELATED = 'RELATED',
  SUPERSEDES = 'SUPERSEDES',
  SUPERSEDED_BY = 'SUPERSEDED_BY',
  COMPANION = 'COMPANION',
  CLONES = 'CLONES',
  CLONED_BY = 'CLONED_BY'
}

export const CASE_LINK_TYPE_LABELS: Record<CaseLinkType, string> = {
  [CaseLinkType.RELATED]: 'Related',
  [CaseLinkType.SUPERSEDES]: 'Supersedes',
  [CaseLinkType.SUPERSEDED_BY]: 'Superseded By',
  [CaseLinkType.COMPANION]: 'Companion',
  [CaseLinkType.CLONES]: 'Clones',
  [CaseLinkType.CLONED_BY]: 'Cloned By'
};

export const CASE_LINK_TYPE_DESCRIPTIONS: Record<CaseLinkType, string> = {
  [CaseLinkType.RELATED]: 'General association between cases',
  [CaseLinkType.SUPERSEDES]: 'This case replaces or continues from the target case',
  [CaseLinkType.SUPERSEDED_BY]: 'This case was replaced or continued by the target case',
  [CaseLinkType.COMPANION]: 'Cases running concurrently that are contextually related',
  [CaseLinkType.CLONES]: 'This case was created as a clone of the target case',
  [CaseLinkType.CLONED_BY]: 'The target case was created as a clone of this case'
};

/**
 * Link types a user can pick when manually linking cases/evictions. CLONES/CLONED_BY are
 * created automatically when a case is cloned and are intentionally excluded here.
 */
export const MANUAL_CASE_LINK_TYPES: CaseLinkType[] = [
  CaseLinkType.RELATED,
  CaseLinkType.SUPERSEDES,
  CaseLinkType.SUPERSEDED_BY,
  CaseLinkType.COMPANION
];

export interface LinkedCase {
  linked_case_id: string;
  case_id: string;
  link_type: CaseLinkType;
  linked_date: string;
  linked_by_id?: string;
  linked_by_name?: string;
  /** EE-531: "EasyEviction support (on behalf of X)" for a support-session act, else the plain name. Never the email. */
  linked_by_display?: string | null;
  impersonated?: boolean;
}

export type CaseLinkAction = 'LINKED' | 'UNLINKED';

export interface LinkedCaseAuditEntry {
  linked_case_id: string;
  case_id: string;
  action: CaseLinkAction;
  link_type: CaseLinkType;
  actor_id?: string;
  actor_name?: string;
  /** EE-531: "EasyEviction support (on behalf of X)" for a support-session act, else the plain name. Never the email. */
  actor_display?: string | null;
  timestamp: string;
}

/** A resolved tag for display: value plus the current org's vocabulary name/color. */
export interface CaseTag {
  value: string;
  name?: string;
  color?: string;
}

/** An organization participating on a case. */
export interface CaseParty {
  org_id: string;
  role: string;
  name?: string;
}

export interface Case extends Auditing {
  id: string;
  case_id: string;
  name: string;
  owner_id: string;
  template_id: string;
  template_name: string;
  property_id?: string;
  data: Record<string, any>;
  /** Backend-computed values for smart (calculated/offset) registry fields; display only, never persisted. */
  computed?: Record<string, any>;
  status: CaseStatus;
  workflow_steps: CaseWorkflowStep[];
  current_step_name: string;
  current_step_number: number;
  current_step_start_date: string;
  // Key-based workflow reference + step progress. Optional so legacy
  // cases fall back to current_step_number/current_step_name.
  workflow_id?: string;
  current_step_key?: string;
  step_progress?: CaseStepProgress[];
  /**
   * Denormalized court-date read-model scalars (Stage 0.5 Wave 3), written through from the case's
   * `COURT_DATE` events. Display-only here (the list sorts on `next_court_date`); the source of truth is
   * the court-date events surfaced via the court-dates panel.
   */
  next_court_date?: string;
  last_court_date?: string;
  last_court_result?: string;
  /** Denormalized links to the reusable tenant-directory entries (S0.1); used to open the tenant history (Wave 9). */
  tenant_record_ids?: string[];
  /** The current org's tags, resolved against its "case" vocabulary (viewer-resolved). */
  tags: CaseTag[];
  /** Org parties on the case (owner + others). */
  parties?: CaseParty[];
  attachments: Attachment[];
  notes: CaseNote[];
  location_ids: string[];
  assigned_user_ids: string[];
  open_date: string;
  closed_date?: string;
  /** Why the case closed: a template category, set for every close. */
  close_reason_category?: string;
  close_reason_note?: string;
  batch_id?: string;
  /** WB-16: marked ready to escalate by a mid-month list update. No attorney hand-off. */
  ready_to_escalate?: boolean;
  ready_to_escalate_date?: string;
  ready_to_escalate_batch_id?: string;
  linked_cases?: LinkedCase[];
  linked_case_audit?: LinkedCaseAuditEntry[];
  /** Stage 1/2: the attorney handoff, when this case has been submitted. Absent for standalone cases. */
  handoff?: CaseHandoff;
}

/**
 * Stage 1/2: the thin PM→attorney handoff record surfaced on a case. `resulting_eviction_case_id` (set at
 * ACCEPTED) backs the live-status read + one-click deep-link to the attorney eviction.
 */
export interface CaseHandoff {
  id: string;
  source_case_id: string;
  source_case_display_id?: string;
  attorney_org_id?: string;
  property_id?: string;
  resulting_eviction_case_id?: string;
  status: 'SUBMITTED' | 'ACCEPTED' | 'REJECTED' | 'CANCELLED';
  submitted_at?: string;
  decided_at?: string;
  rejection_reason?: string;
}

/** A connected attorney the PM may submit a case to (the submit picker). */
export interface AttorneyConnection {
  attorney_org_id: string;
  attorney_name?: string;
}

/**
 * Result of `POST /cases/{id}/escalate` (Start Self-Managed Eviction): the closed delinquency
 * case plus the spawned eviction case's ids, for direct navigation to the successor.
 */
export interface EscalateCaseResult {
  case: Case;
  spawned_case_id: string;
  spawned_case_display_id?: string;
}

/** A source field that carries over automatically at escalation: identical key or saved template. */
export interface EscalationMappingEntry {
  source_key: string;
  source_label?: string;
  target_key: string;
  target_label?: string;
  origin: 'exact' | 'template';
}

/** An AI-suggested mapping for a source field with no automatic target (user confirms). */
export interface EscalationMappingSuggestion {
  source_key: string;
  source_label?: string;
  target_key: string;
  target_label?: string;
  confidence: number;
}

/** A bare field reference (unmapped source field, or a target-required field starting empty). */
export interface EscalationMappingField {
  key: string;
  label?: string;
}

/** The escalation wizard's data-mapping preview (`GET /cases/{id}/escalation-mapping`). */
export interface EscalationMappingPreview {
  mapped: EscalationMappingEntry[];
  suggestions: EscalationMappingSuggestion[];
  unmapped: EscalationMappingField[];
  missing_required: EscalationMappingField[];
  template_id?: string;
  escalation_scope?: string;
}

/** Tiered plaintiff suggestion offered to the attorney at accept (always overridable). */
export interface PlaintiffSuggestion {
  client_id?: string;
  client_name?: string;
  /** 'learned' = property→plaintiff map (authoritative for property-linked intakes); 'name' = company-name match. */
  source: 'learned' | 'name' | 'none';
}

export interface CaseWorkflowStepTemplate {
  id: string;
  step: number;
  name: string;
  description?: string;
  icon?: string;
  optional?: boolean;
  /** Go/No-Go decision step (options-level; live-editable on in-use workflows). */
  decision?: boolean;
  survey_id?: string;
  /** @deprecated Legacy id list, dead on the PM path — not read or written. Use {@link document_generation}. */
  document_templates?: string[];
  /** Unified per-document config: template id + hide-from-client + optional conditional rule. */
  document_generation?: DocumentGenerationEntry[];
  expected_days?: number;
  email_on_completion?: boolean;
  assign_user_ids: string[];
  // Tier B (P5): per-step assignee role. Optional/additive.
  step_key?: string;
  assignee_role?: string;
}

/**
 * A step's unified document-generation entries as fresh, deeply-cloned copies (callers mutate
 * freely without touching the original step config — including the nested `conditional_rule`,
 * its conditions, and each condition's flat `source_field` and (possibly array) `value`).
 * `document_generation` is the sole source of step documents on the PM path — the legacy
 * `document_templates` id list is dead and deliberately ignored.
 */
export function toDocumentGenerationEntries(
  step: { document_generation?: DocumentGenerationEntry[] } | null | undefined
): DocumentGenerationEntry[] {
  return (step?.document_generation ?? []).map(e => ({
    ...e,
    conditional_rule: e.conditional_rule
      ? {
        conditions: (e.conditional_rule.conditions ?? []).map(c => ({
          ...c,
          source_field: c.source_field ? {...c.source_field} : c.source_field,
          value: Array.isArray(c.value) ? [...c.value] : c.value
        }))
      }
      : e.conditional_rule
  }));
}

/**
 * A named workflow within a {@link CaseTemplate}. A case type may define several
 * (one default). Independent of the attorney `WorkflowVersion` model — no
 * email/billable/court settings.
 */
export interface CaseWorkflow {
  id: string;
  name: string;
  is_default: boolean;
  steps: CaseWorkflowStepTemplate[];
}

export interface ReportFieldMapping {
  field_key: string;
  display_label: string;
  field_type: string;
  include_in_grid: boolean;
  include_in_report: boolean;
}

/** A terminal business outcome a case of a given type can be closed with (S0-R1). */
export interface CaseDisposition {
  value: string;
  label: string;
  closes_case?: boolean;
  action?: string;
  counts_in_resolution_rate?: boolean;
  /** Requires a non-blank close reason note when chosen (server-enforced in closeCase). */
  requires_note?: boolean;
}

/** Non-binding tenant strike-hold suggestion (S0.3). */
export interface StrikeSuggestion {
  message: string;
  severity: string;
  suggested_hold_days: number;
  tenant_record_id: string;
  strike_count: number;
}

/** Org-configurable tenant recidivism / strike-hold rule (S0.3). */
export interface TenantStrikeRule {
  enabled: boolean;
  strike_threshold: number;
  window_days: number;
  hold_days: number;
}

/** Per-property delinquency rollup (S0.2). */
export interface PropertyAnalyticsRow {
  property_id: string;
  case_count: number;
  sum_past_due_rent: number;
  escalation_rate: number;
  cure_rate: number;
  recidivist_tenant_count: number;
}

/** Portfolio delinquency funnel (S0.2). */
export interface PortfolioFunnel {
  active: number;
  cured: number;
  escalated: number;
  submitted: number;
  dollars_at_risk: number;
}

/** Full per-property dashboard (Stage 3, S3-R1.3). */
export interface PropertyDashboard {
  property_id: string;
  name?: string;
  address?: string;
  owner_name?: string;
  unit_count: number;
  analytics?: PropertyAnalyticsRow;
  status_breakdown: Record<string, number>;
  submitted_to_attorney_count: number;
  cases: PropertyDashboardCase[];
}

export interface PropertyDashboardCase {
  id: string;
  case_id: string;
  name: string;
  status: string;
  current_step_name?: string;
  disposition?: string;
  open_date?: string;
  handoff_status?: string;
  resulting_eviction_case_id?: string;
}

export interface CaseTemplate extends Auditing {
  id: string;
  name: string;
  description?: string;
  organization_id: string;
  status: PublicationStatus;
  field_registry?: FieldDefinition[];
  /** The intake form (EE-412): pages, sections, rows and rules over the registry. */
  intake_layout?: FormLayout;
  sidebar_projection?: SidebarProjection;
  workflow_steps: CaseWorkflowStepTemplate[];
  document_template_ids: string[];
  case_id_prefix?: string;
  case_id_sequence: number;
  report_field_mappings: ReportFieldMapping[];
  name_template?: string;
  /** Named workflows for this case type (multiple per type, one default). */
  workflows?: CaseWorkflow[];
  /**
   * Configured close-reason categories offered when closing a case of this type
   * (Req 6.1, 6.4). When empty/absent the platform defaults are used.
   */
  close_reason_categories?: string[];
  /**
   * Terminal business outcomes offered when closing a case of this type (S0-R1). When non-empty a
   * disposition is required at close; when absent the case closes with a category only.
   */
  dispositions?: CaseDisposition[];
  /** True for admin-owned, org-agnostic shared catalog templates. */
  shared?: boolean;
  /** Set on an org adoption — the shared template it inherits built-in fields from. */
  parent_template_id?: string;
}

export interface CaseFilterParams {
  status?: CaseStatus;
  template_id?: string;
  search?: string;
  tags?: string[];
  /** Filter to cases linked to this property (S0.1). */
  property_id?: string;
  page?: number;
  size?: number;
  /** Spring sort, e.g. `id,asc`. Paged loads set one so pages are stable. */
  sort?: string;
}

export interface CaseReportFilter {
  start_date?: string;
  end_date?: string;
  status?: CaseStatus;
  location_ids?: string[];
}

export interface ImportCreateCasesDTO {
  file_id: string;
  template_id: string;
  field_mappings: Record<string, string>;
  /** Wave 5: undefined = create every row; 'update' = upsert the matching open case; 'skip' = skip duplicates. */
  dedupe_mode?: 'update' | 'skip';
}

export interface ImportSubmitDTO {
  file_id: string;
  field_mappings: Record<string, string>;
}
